// components/private/order/PicMembersList.tsx
// Daftar anggota/delegasi yang didaftarkan oleh PIC - ditampilkan di halaman
// status pembayaran supaya PIC bisa lihat siapa saja yang tercakup dalam 1 tagihan kolektif ini.
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import {
    Users2,
    Search,
    Mail,
    Building2,
    ChevronLeft,
    ChevronRight,
    AlertCircle,
} from "lucide-react";
import type { PicApiResponse, ParticipantData, MetaData } from "@/types/pic";

interface PicMembersListProps {
    activePeriodId: number;
}

const formatRupiah = (number: number) => {
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(number);
};

export function PicMembersList({ activePeriodId }: PicMembersListProps) {
    const [participants, setParticipants] = useState<ParticipantData[]>([]);
    const [meta, setMeta] = useState<MetaData | null>(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    // Debounce pencarian
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const fetchMembers = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axiosClient.get<PicApiResponse>(`/pic/participants`, {
                params: { period_id: activePeriodId, page, per_page: 10, search },
            });
            setParticipants(data.data);
            setMeta(data.meta);
        } catch (error) {
            toast.error("Gagal memuat daftar anggota.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [activePeriodId, page, search]);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    return (
        <Card className="border-0 shadow-xl bg-white dark:bg-slate-900 rounded-[2.5rem] overflow-hidden">
            <CardHeader className="p-8 pb-4 flex flex-row items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-blue-600 rounded-xl shadow-lg shadow-blue-200 dark:shadow-blue-900">
                        <Users2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="text-sm font-black uppercase tracking-widest text-slate-900 dark:text-white">
                            Anggota Terdaftar
                        </h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            {meta?.total ?? 0} orang tercakup dalam tagihan kolektif ini
                        </p>
                    </div>
                </div>
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Cari nama/email..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-sm"
                    />
                </div>
            </CardHeader>

            <CardContent className="p-8 pt-0">
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {loading ? (
                        [...Array(3)].map((_, i) => (
                            <div key={i} className="py-4"><Skeleton className="h-12 w-full rounded-xl" /></div>
                        ))
                    ) : participants.length > 0 ? (
                        participants.map((p) => (
                            <div key={p.participant_id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{p.full_name}</p>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                                        <span className="flex items-center gap-1 truncate max-w-[220px]">
                                            <Mail className="w-3 h-3 shrink-0" /> {p.email}
                                        </span>
                                        {p.institution && (
                                            <span className="flex items-center gap-1 truncate max-w-[220px]">
                                                <Building2 className="w-3 h-3 shrink-0" /> {p.institution}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0 sm:justify-end">
                                    <div className="text-right">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{p.registration?.package_name || "-"}</p>
                                        <p className="text-xs font-black text-slate-700 dark:text-slate-200 font-mono">
                                            {formatRupiah(p.registration?.price || 0)}
                                        </p>
                                    </div>
                                    <Badge className={
                                        p.registration?.payment_status === "paid"
                                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 font-bold shadow-none"
                                            : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border-rose-200 dark:border-rose-800 font-bold shadow-none"
                                    }>
                                        {p.registration?.payment_status === "paid" ? "Lunas" : "Belum Lunas"}
                                    </Badge>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="py-16 text-center text-slate-400">
                            <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-20" />
                            <p className="text-sm font-medium">
                                {search ? "Anggota tidak ditemukan." : "Belum ada anggota yang terdaftar."}
                            </p>
                        </div>
                    )}
                </div>

                {meta && meta.last_page > 1 && (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            Halaman {meta.current_page} / {meta.last_page}
                        </span>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 rounded-lg text-xs font-bold"
                                disabled={page <= 1 || loading}
                                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            >
                                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Prev
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-3 rounded-lg text-xs font-bold"
                                disabled={page >= meta.last_page || loading}
                                onClick={() => setPage((prev) => Math.min(meta.last_page, prev + 1))}
                            >
                                Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
